/**
 * Prompt Suggestions Panel
 *
 * Displays AI-generated prompt suggestions based on current context.
 * Shows 3-4 contextual suggestions that user can click to use.
 *
 * Rendered as a dismissible panel above the chat input.
 */

import React from 'react';
import { Sparkles, X, Lightbulb } from 'lucide-react';
import type { DynamicSuggestion } from '@ctrl-enter/types';
import { cn } from '../utils/cn';

type PromptSuggestionsPanelProps = {
  suggestions: DynamicSuggestion[];
  isVisible: boolean;
  onClose: () => void;
  onSelectSuggestion: (suggestion: DynamicSuggestion) => void;
};

export const PromptSuggestionsPanel: React.FC<PromptSuggestionsPanelProps> = ({
  suggestions,
  isVisible,
  onClose,
  onSelectSuggestion,
}) => {
  if (!isVisible || suggestions.length === 0) return null;

  return (
    <div
      className={cn(
        'mb-3 p-4 bg-primary/5 border border-primary/20 rounded-lg',
        'animate-in slide-in-from-bottom-4 fade-in duration-300'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-foreground">Suggested Actions</span>
          <span className="text-xs text-muted-foreground">Based on your current context</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-accent rounded transition-colors"
          aria-label="Close suggestions"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {/* Suggestion chips */}
      <div className="flex flex-wrap gap-2">
        {suggestions.map(suggestion => (
          <button
            key={suggestion.id}
            onClick={() => onSelectSuggestion(suggestion)}
            className={cn(
              'px-4 py-2 text-sm text-foreground',
              'bg-background border border-border rounded-lg',
              'hover:bg-accent hover:border-primary/40 transition-all',
              'flex items-center gap-2',
              'shadow-sm hover:shadow-md'
            )}
          >
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span>{suggestion.label}</span>
          </button>
        ))}
      </div>

      {/* Help text */}
      <div className="mt-3 text-xs text-muted-foreground flex items-start gap-1.5">
        <Lightbulb className="h-3 w-3 mt-0.5 flex-shrink-0" />
        <span>
          These suggestions are AI-generated based on your screen capture and chat history. Click
          one to use it, or close this panel to type your own message.
        </span>
      </div>
    </div>
  );
};
